import type { TFunction } from "i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DataTable, dataTableFeatures } from "@/components/ui/data-table";
import { MODAL_PAGE_SIZE } from "@/lib/paging";
import type { Korps } from "./useWedstrijden";

const helper = createColumnHelper<typeof dataTableFeatures, Korps>();

const kolommen = (t: TFunction<"wedstrijden">) =>
  helper.columns([
    helper.accessor("name", {
      header: t("korpsKiezer.korps"),
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    helper.accessor("city", { header: t("korpsKiezer.plaats") }),
  ]);

/**
 * `useBeheerKorpsen`, not `me.korpsen`, which answers a different question. A table, because only
 * the plaats column tells two "Jeugdbrandweer Noord" apart. The heading is a `<span>`, or its
 * `<label for>` would run into the search box's own accessible name.
 */
export function KorpsKiezer({
  label,
  korpsen,
  gekozenId,
  onKies,
  onWis,
}: {
  label: string;
  korpsen: Korps[];
  gekozenId: string | undefined;
  onKies: (korpsId: string) => void;
  onWis: () => void;
}) {
  const { t } = useTranslation("wedstrijden");

  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{t("korpsKiezer.legend")}</span>

        {/* Not a row: an absent choice would otherwise sort and search as if it were a plaats. */}
        {gekozenId ? (
          <Button type="button" variant="ghost" size="sm" onClick={onWis}>
            {t("korpsKiezer.wissen")}
          </Button>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("korpsKiezer.nogNietBekend")}
          </span>
        )}
      </div>
      <DataTable
        label={label}
        data={korpsen}
        columns={kolommen(t)}
        getRowId={(row) => row.id}
        selectedId={gekozenId}
        onSelect={onKies}
        searchPlaceholder={t("korpsKiezer.zoek")}
        empty={t("korpsKiezer.leeg")}
        clientPageSize={MODAL_PAGE_SIZE}
        pagerVariant="compact"
      />
    </div>
  );
}
